import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Textarea,
  Select,
  Button,
  Spinner,
  Input,
} from "../ui";
import SeatGrid from "./SeatGrid";
import {
  createAssignment,
  updateAssignment,
  createAssignmentPortal,
  updateAssignmentPortal,
  getAvailableVehicles,
  getAvailableVehiclesPortal,
  getVehicleCapacity,
  getVehicleCapacityPortal,
  lookupTransportCharge,
  lookupTransportChargePortal,
  getAllPincodeRates,
  getAllPincodeRatesPortal,
} from "../../api/transport/transportApi";
import { useToast } from "../../context/ToastContext";

const AssignmentModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  isPortal = false,
  student = null,
  academicYearId = "",
  academicYearOptions = [],
}) => {
  const [form, setForm] = useState({
    academicYearId: "",
    pincode: "",
    vehicleId: "",
    transportAddress: "",
    routeDescription: "",
    seatNumber: null,
    remarks: "",
  });
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [charge, setCharge] = useState(null);
  const [capacityInfo, setCapacityInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const toast = useToast();

  const studentId =
    editData?.studentId?._id || editData?.studentId || student?._id;

  // Load unique pincodes from active rates (for searchable dropdown)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoadingPincodes(true);
      try {
        const fn = isPortal ? getAllPincodeRatesPortal : getAllPincodeRates;
        const res = await fn({ isActive: "true" });
        const rates = res.data || res || [];
        const map = new Map();
        rates.forEach((r) => {
          const pin = String(r.pincode || "").trim();
          if (!pin) return;
          if (!map.has(pin)) {
            map.set(pin, {
              value: pin,
              label: r.areaName ? `${pin} — ${r.areaName}` : pin,
            });
          }
        });
        setPincodeOptions(Array.from(map.values()).sort((a, b) => a.value.localeCompare(b.value)));
      } catch {
        setPincodeOptions([]);
      } finally {
        setLoadingPincodes(false);
      }
    })();
  }, [isOpen, isPortal]);

  useEffect(() => {
    if (!isOpen) return;
    setCharge(null);
    setCapacityInfo(null);
    if (editData) {
      setForm({
        academicYearId:
          editData.academicYearId?._id ||
          editData.academicYearId ||
          academicYearId ||
          "",
        pincode: editData.pincode || "",
        vehicleId: editData.vehicleId?._id || editData.vehicleId || "",
        transportAddress: editData.transportAddress || "",
        routeDescription: editData.routeDescription || "",
        seatNumber: editData.seatNumber ?? null,
        remarks: editData.remarks || "",
      });
    } else {
      setForm({
        academicYearId: academicYearId || "",
        pincode: "",
        vehicleId: "",
        transportAddress: "",
        routeDescription: "",
        seatNumber: null,
        remarks: "",
      });
      setVehicles([]);
    }
  }, [isOpen, editData, academicYearId]);

  const loadVehicles = useCallback(async () => {
    if (!form.pincode || !form.academicYearId) {
      setVehicles([]);
      return;
    }
    setLoadingVehicles(true);
    try {
      const fn = isPortal ? getAvailableVehiclesPortal : getAvailableVehicles;
      const res = await fn({
        pincode: form.pincode.trim(),
        academicYearId: form.academicYearId,
        includeFull: editData ? "true" : undefined,
      });
      setVehicles(res.data || res || []);
    } catch {
      setVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  }, [form.pincode, form.academicYearId, isPortal, editData]);

  useEffect(() => {
    if (isOpen) loadVehicles();
  }, [isOpen, loadVehicles]);

  // Charge when vehicle selected
  useEffect(() => {
    const run = async () => {
      if (!form.vehicleId || !form.pincode) {
        setCharge(null);
        return;
      }
      const v = vehicles.find((x) => String(x._id) === String(form.vehicleId));
      if (!v) return;
      try {
        const fn = isPortal ? lookupTransportChargePortal : lookupTransportCharge;
        const res = await fn({
          pincode: form.pincode.trim(),
          vehicleType: v.vehicleType,
        });
        setCharge(res.data || res);
      } catch {
        setCharge(null);
      }
    };
    run();
  }, [form.vehicleId, form.pincode, vehicles, isPortal]);

  // Capacity / seat map
  useEffect(() => {
    const run = async () => {
      if (!form.vehicleId || !form.academicYearId) {
        setCapacityInfo(null);
        return;
      }
      try {
        const fn = isPortal ? getVehicleCapacityPortal : getVehicleCapacity;
        const res = await fn(form.vehicleId, {
          academicYearId: form.academicYearId,
        });
        setCapacityInfo(res.data || res);
      } catch {
        setCapacityInfo(null);
      }
    };
    run();
  }, [form.vehicleId, form.academicYearId, isPortal]);

  // Vehicle dropdown: name + number
  const vehicleOptions = vehicles.map((v) => ({
    value: v._id,
    label: `${v.name || "Vehicle"} — ${v.vehicleNumber || ""}${
      v.availableSeats != null ? ` (${v.availableSeats} seats left)` : ""
    }${v.isFull ? " [FULL]" : ""}`,
  }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!studentId) {
      toast.error("Student is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        studentId,
        academicYearId: form.academicYearId,
        vehicleId: form.vehicleId,
        pincode: form.pincode.trim(),
        transportAddress: form.transportAddress.trim(),
        routeDescription: form.routeDescription,
        seatNumber: form.seatNumber,
        remarks: form.remarks,
      };

      if (editData?._id) {
        if (isPortal) await updateAssignmentPortal(editData._id, payload);
        else await updateAssignment(editData._id, payload);
      } else {
        if (isPortal) await createAssignmentPortal(payload);
        else await createAssignment(payload);
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save assignment");
    } finally {
      setLoading(false);
    }
  };

  const studentLabel = student
    ? `${student.firstName || ""} ${student.lastName || ""}`.trim() +
      (student.admissionNo ? ` (${student.admissionNo})` : "")
    : editData?.studentId
      ? `${editData.studentId.firstName || ""} ${editData.studentId.lastName || ""}`.trim()
      : "—";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? "Edit Transport Assignment" : "Assign Transport"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading}>
            {editData ? "Update" : "Assign"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
      >
        

        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 px-3 py-2 text-sm">
          <span className="text-neutral-500 dark:text-neutral-400">Student: </span>
          <span className="font-medium text-neutral-900 dark:text-neutral-100">{studentLabel}</span>
        </div>

        <Select
          label="Academic year"
          options={academicYearOptions}
          value={form.academicYearId}
          onChange={(v) =>
            setForm((p) => ({
              ...p,
              academicYearId: v,
              vehicleId: "",
              seatNumber: null,
            }))
          }
          placeholder="Select academic year"
        />

        {/* Pincode — searchable dropdown from defined rates */}
        <Select
          label="Pincode"
          options={pincodeOptions}
          value={form.pincode}
          onChange={(v) =>
            setForm((p) => ({
              ...p,
              pincode: v,
              vehicleId: "",
              seatNumber: null,
            }))
          }
          placeholder={
            loadingPincodes ? "Loading pincodes..." : "Search or select pincode"
          }
          searchable
          disabled={loadingPincodes}
        />
        {!loadingPincodes && pincodeOptions.length === 0 && (
          <p className="text-xs text-warning-700 -mt-2">
            No active pincode rates found. Add rates first.
          </p>
        )}

        {/* Vehicle — select by name + number (not free text) */}
        <div>
          <Select
            label="Vehicle (name / number)"
            options={vehicleOptions}
            value={form.vehicleId}
            onChange={(v) =>
              setForm((p) => ({ ...p, vehicleId: v, seatNumber: null }))
            }
            placeholder={
              loadingVehicles
                ? "Loading vehicles..."
                : form.pincode
                  ? "Select vehicle"
                  : "Select pincode first"
            }
            searchable
            disabled={!form.pincode || !form.academicYearId || loadingVehicles}
          />
          {loadingVehicles && (
            <div className="mt-1">
              <Spinner size="sm" label="Loading available vehicles" />
            </div>
          )}
          {!loadingVehicles && form.pincode && vehicles.length === 0 && (
            <p className="mt-1 text-xs text-warning-700">
              No vehicles with free seats for this pincode.
            </p>
          )}
        </div>

        {charge && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm">
            <span className="text-neutral-600 dark:text-neutral-300">Monthly charge: </span>
            <span className="font-semibold text-primary-900 dark:text-primary-300">
              ₹{Number(charge.monthlyCharge || 0).toFixed(2)}
            </span>
            {charge.areaName ? (
              <span className="ml-2 text-neutral-500 dark:text-neutral-400">({charge.areaName})</span>
            ) : null}
          </div>
        )}

        <Textarea
          label="Transport address"
          rows={2}
          placeholder="Full pickup / drop address"
          value={form.transportAddress}
          onChange={(e) =>
            setForm((p) => ({ ...p, transportAddress: e.target.value }))
          }
          required
        />

        <Textarea
          label="Route description"
          rows={2}
          placeholder="Optional route notes"
          value={form.routeDescription}
          onChange={(e) =>
            setForm((p) => ({ ...p, routeDescription: e.target.value }))
          }
        />

        {form.vehicleId && capacityInfo && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
              Select seat (optional)
            </p>
            <SeatGrid
              capacity={capacityInfo.capacity}
              occupiedMap={capacityInfo.seatMap || []}
              selectedSeat={form.seatNumber}
              onSelectSeat={(n) => setForm((p) => ({ ...p, seatNumber: n }))}
            />
          </div>
        )}

        <Input
          label="Remarks"
          value={form.remarks}
          onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
        />
      </form>
    </Modal>
  );
};

export default AssignmentModal;
